import type { CartProduct } from '@/interfaces'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface State {
  cart: CartProduct[]

  getTotalItems: () => number
  getSummaryInformation: () => {
    subTotal: number
    total: number
    itemsInCart: number
  }

  addProductTocart: (product: CartProduct) => void
  updateProductQuantity: (product: CartProduct, quantity: number) => void
  removeProduct: (product: CartProduct) => void

  clearCart: () => void
}

export const useCartStore = create<State>()(
  persist(
    (set, get) => ({
      cart: [], // Estado inicial vacío

      // Métodos
      getTotalItems: () => {
        const { cart } = get()
        return cart.reduce((total, item) => total + item.quantity, 0)
      },

      getSummaryInformation: () => {
        const { cart } = get()

        const subTotal = cart.reduce(
          (subTotal, product) => product.quantity * product.price + subTotal,
          0
        )
        const total = subTotal
        const itemsInCart = cart.reduce(
          (total, item) => total + item.quantity,
          0
        )

        return {
          subTotal,
          total,
          itemsInCart
        }
      },

      addProductTocart: (product: CartProduct) => {
        const { cart } = get()

        // 1. Revisar si el producto existe en el carrito con el tamaño seleccionado
        const productInCart = cart.some(
          (item) => item.id === product.id && item.size === product.size
        )

        if (!productInCart) {
          set({ cart: [...cart, product] })
          return
        }

        // 2. Si el producto existe para el tamaño seleccionado, incrementar la cantidad
        const updatedCartProducts = cart.map((item) => {
          if (item.id === product.id && item.size === product.size) {
            return { ...item, quantity: item.quantity + product.quantity }
          }

          return item
        })

        set({ cart: updatedCartProducts })
      },

      updateProductQuantity: (product: CartProduct, quantity: number) => {
        const { cart } = get()

        const updatedCartProducts = cart.map((item) => {
          if (item.id === product.id && item.size === product.size) {
            return { ...item, quantity: quantity }
          }
          return item
        })

        set({ cart: updatedCartProducts })
      },

      removeProduct: (product: CartProduct) => {
        const { cart } = get()
        const updatedCartProducts = cart.filter(
          (item) => item.id !== product.id || item.size !== product.size
        )

        set({ cart: updatedCartProducts })
      },

      clearCart: () => {
        set({ cart: [] })
      }
    }),
    {
      name: 'shopping-cart' // Nombre del almacenamiento
    }
  )
)

// Establecer el estado después de la creación del store
useCartStore.setState((state) => {
  const cartFromStorage = state.cart || []

  if (cartFromStorage.length > 0) {
    return { cart: cartFromStorage }
  }

  // Si no hay productos en el carrito, no necesitas devolver nada
  return {}
})
